import { useEffect, useMemo, useRef, useState } from 'react';
import { CornerDownLeft, Search } from 'lucide-react';
import Icon from './Icon';
import Kbd from './Kbd';
import { useDialogFocus } from './Modal';

// Ctrl K / ⌘K opens the palette anywhere on Console and Workbench (never on Portal).
export function useCommandPaletteHotkey(setOpen) {
  useEffect(() => {
    const onKey = e => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(o => !o); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [setOpen]);
}

// §2.5 Command palette — SHELL at HD-3: filters the items it is given; search across scripts, productions,
// clients and footage is wired by the shell (HD-4). items: [{ id, label, group, meta?, icon?, onSelect }];
// source(query) → items replaces the built-in filter when given; loading shows a line while the source warms up.
export default function CommandPalette({ open, onOpenChange, items = [], source, loading = false, placeholder = 'Jump to a page' }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const box = useRef(null);
  useDialogFocus(open, box, () => onOpenChange(false));
  useEffect(() => { if (open) { setQ(''); setActive(0); } }, [open]);
  const shown = useMemo(() => { if (source) return source(q); const k = q.trim().toLowerCase(); return k ? items.filter(i => i.label.toLowerCase().includes(k)) : items; }, [items, q, source]);
  if (!open) return null;
  const run = it => { onOpenChange(false); it.onSelect?.(); };
  let lastGroup = null;
  return (
    <div className="ui ui-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onOpenChange(false); }}>
      <div className="ui-cmdk" role="dialog" aria-modal="true" aria-label="Command palette" ref={box}>
        <div className="ui-cmdk__field">
          <Icon icon={Search} />
          <input className="ui-cmdk__input" placeholder={placeholder} value={q} data-autofocus role="combobox" aria-expanded="true" aria-controls="ui-cmdk-list"
            aria-activedescendant={shown[active] ? `ui-cmdk-${shown[active].id}` : undefined}
            onChange={e => { setQ(e.target.value); setActive(0); }}
            onKeyDown={e => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, shown.length - 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
              if (e.key === 'Enter' && shown[active]) { e.preventDefault(); run(shown[active]); }
            }} />
        </div>
        <ul className="ui-cmdk__list" role="listbox" id="ui-cmdk-list">
          {loading && <li className="ui-cmdk__group" role="status">Loading scripts, productions, clients and folders…</li>}
          {shown.length === 0 && !loading && <li className="ui-cmdk__group">Nothing matches. Try a script title, a client or a page name.</li>}
          {shown.map((it, i) => {
            const head = it.group !== lastGroup ? <li key={`g-${it.group}`} className="ui-cmdk__group" role="presentation">{it.group}</li> : null;
            lastGroup = it.group;
            return [head, (
              <li key={it.id} id={`ui-cmdk-${it.id}`} role="option" aria-selected={i === active} className="ui-cmdk__item"
                onMouseEnter={() => setActive(i)} onMouseDown={e => { e.preventDefault(); run(it); }}>
                {it.icon && <Icon icon={it.icon} />}{it.label}{it.meta && <span className="ui-meta">{it.meta}</span>}
              </li>
            )];
          })}
        </ul>
        <div className="ui-cmdk__foot"><span><Icon icon={CornerDownLeft} /></span><span>Open</span><Kbd>Esc</Kbd><span>Close</span></div>
      </div>
    </div>
  );
}
